import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import LoadingSpinner from "@/presentation/components/shared/LoadingSpinner";

interface AppInitialLoaderContextValue {
  showInitialLoader: () => void;
  dismissInitialLoader: () => void;
}

const AppInitialLoaderContext = createContext<AppInitialLoaderContextValue | null>(null);

interface AppInitialLoaderProviderProps {
  children: ReactNode;
}

export function AppInitialLoaderProvider({ children }: Readonly<AppInitialLoaderProviderProps>) {
  const [visible, setVisible] = useState(false);

  const showInitialLoader = useCallback(() => {
    setVisible(true);
  }, []);

  const dismissInitialLoader = useCallback(() => {
    setVisible(false);
  }, []);

  const value = useMemo(
    () => ({
      showInitialLoader,
      dismissInitialLoader,
    }),
    [showInitialLoader, dismissInitialLoader],
  );

  return (
    <AppInitialLoaderContext.Provider value={value}>
      {visible ? <LoadingSpinner /> : null}
      {children}
    </AppInitialLoaderContext.Provider>
  );
}

function useAppInitialLoaderContext(): AppInitialLoaderContextValue | null {
  return useContext(AppInitialLoaderContext);
}

/** Show the full-screen bootstrap loader (Auth0 init, session fetch, etc.). */
export function useShowInitialLoader(active: boolean): void {
  const context = useAppInitialLoaderContext();

  useEffect(() => {
    if (active) {
      context?.showInitialLoader();
    }
  }, [active, context]);
}

/** Hide the full-screen bootstrap loader once a page has mounted (e.g. Dashboard). */
export function useDismissInitialLoader(): void {
  useDismissInitialLoaderWhen(true);
}

/** Hide the loader when a condition is true (e.g. on public auth routes). */
export function useDismissInitialLoaderWhen(active: boolean): void {
  const context = useAppInitialLoaderContext();

  useEffect(() => {
    if (active) {
      context?.dismissInitialLoader();
    }
  }, [active, context]);
}

/** Allow error screens (session failure, etc.) to replace the bootstrap loader. */
export function useDismissInitialLoaderOnError(active: boolean): void {
  useDismissInitialLoaderWhen(active);
}
